import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import type { AgentPageContext } from '@/modules/personal-agent-api';
import type { Signal } from '@/modules/score-api';
import { track } from '@/shared/utils/track';

// `null` is the generic assistant; a market scopes the panel to that
// market's call; an activity scopes it to a market's opened whale activity
// — `pageContext` is what the drawer showed, built at the click and sent
// with the first question.
export type AiScope =
  | null
  | { kind: 'market'; signal: Signal }
  | { kind: 'activity'; signal: Signal; pageContext: AgentPageContext };

// A jotai atom (the Modal stack precedent), not layout state: the panel is
// mounted once in DefaultLayout while its openers sit in the chrome and in
// page content, and `ai_widget_opened` must fire exactly once per open
// whichever entry was used. The scope outlives close so that reopening
// with the same scope finds its conversation (AiWidget keys the panel on
// it).
const aiAtom = atom<{ open: boolean; scope: AiScope }>({
  open: false,
  scope: null,
});

export function useAiOpen() {
  const [state, setState] = useAtom(aiAtom);
  const openWith = (scope: AiScope) => {
    track(
      'ai_widget_opened',
      scope === null ?
        { scope: 'page' }
      : { scope: scope.kind, signal_id: scope.signal.id },
    );
    setState({ open: true, scope });
  };
  // eslint-disable-next-line no-restricted-syntax -- useAgentComposer subscribes with it: DefaultLayout hands it down as the widget panel's onClose, and the composer's Escape keydown effect lists it in its deps
  const close = useCallback(
    () => setState((s) => ({ ...s, open: false })),
    [setState],
  );
  return {
    open: state.open,
    scope: state.scope,
    openWith,
    toggle: () => {
      if (state.open) close();
      else openWith(null);
    },
    close,
  };
}
